// FindingsPage — 監査指摘 (#findings, blueprint v2 Phase 4 — 2026-07-05)
//
// worker sweep (決定論検査) が検出した指摘 (findings) を一覧・確認・却下・再open する監査画面。
//   - kind: batch_verify_failed=照合不一致 / batch_unverifiable=検証不能 /
//           job_dead_letter=処理不能ジョブ / routine_overdue=ルーチン遅延 / duplicate_deal=重複疑い
//   - severity 色分け: high=赤 / medium=橙 / low=グレー
//   - 却下 (dismiss) は理由必須のインライン入力 (window.confirm/prompt 禁止)
//   - target link: batch系 → #batches/:batchId、 deal → freee 取引画面、 job → id 表示のみ
// デザインは BatchesPage のパレットC準拠。API 未デプロイ時はエラー表示のみで画面は壊れない。

import React, { useCallback, useEffect, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import { MdRefresh, MdSearch, MdWarningAmber } from 'react-icons/md';
import {
  fetchFindings, fetchFindingDetail, confirmFinding, dismissFinding, reopenFinding, sweepFindings,
  dismissFindingNoInvoiceThisCycle, excludeVendorFinding,
  Finding, FindingEvent,
} from '../api/client';
import { findingStatusToUnified, ACTION_HINT } from '../lib/statusVocab';

const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386', line: '#d9e0e7', lineDark: '#bdc8d3',
  navy: '#203a57', good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
const NUM = '"SFMono-Regular",Consolas,Menlo,monospace';
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",sans-serif';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, overflow: 'hidden' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '8px 18px 8px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid rgba(255,255,255,.35)', borderRadius: 13, background: 'rgba(255,255,255,.12)', color: '#e5eef8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  filterBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 18px', background: '#f4f7fa', borderBottom: `1px solid ${C.line}` } as React.CSSProperties,
  filterLabel: { fontSize: 11, color: C.sub, fontWeight: 800, letterSpacing: '.04em' } as React.CSSProperties,
  select: { border: `1px solid ${C.lineDark}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: FONT, background: '#fff', color: C.ink } as React.CSSProperties,
  list: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  // 枠線: 赤=severity high の事実のみ / 橙=あなたの裁定待ち(open) / 他=中立
  card: (frame: 'danger' | 'action' | 'none'): React.CSSProperties => ({ marginBottom: 12, border: `1px solid ${frame === 'danger' ? '#e6b0ac' : frame === 'action' ? '#e3c68a' : C.lineDark}`, borderRadius: 7, background: '#fff', overflow: 'hidden' }),
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 15px', flexWrap: 'wrap', cursor: 'pointer' } as React.CSSProperties,
  badge: (bg: string, fg: string, bd: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 800, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }),
  sub: { color: C.sub, fontSize: 11 } as React.CSSProperties,
  subhead: { fontSize: 11, fontWeight: 800, color: C.sub, letterSpacing: '.05em', marginBottom: 4 } as React.CSSProperties,
  pre: { margin: 0, padding: '10px 12px', background: '#f4f7fa', border: `1px solid ${C.line}`, borderRadius: 5, fontFamily: NUM, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto', color: C.ink } as React.CSSProperties,
  reasonInput: { border: `1px solid ${C.lineDark}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: FONT, minWidth: 240 } as React.CSSProperties,
};

// kind → 日本語ラベル (InboxPage の「監査指摘」セクションでも使用)
export const FINDING_KIND_LABEL: Record<string, string> = {
  batch_verify_failed: '照合不一致',
  batch_unverifiable: '検証不能',
  job_dead_letter: '処理不能ジョブ',
  routine_overdue: 'ルーチン遅延',
  duplicate_deal: '重複疑い',
  invoice_unenrolled_newcomers: '請求書 run未収容',
  timee_invoice_unbuilt: 'タイミー 未提案',
  bank_analysis_stalled: '銀行AI分析 滞留',
  arica_fetch_platform_stuck: '請求書PDF取得 障害',
  arica_fetch_email_stuck: '請求書PDF取得失敗 (メール)',
  vendor_invoice_missing: '請求書 未着',
  vendor_invoice_resumed: '取引先 再着荷 (監視再開)',
  freee_api_update: 'freee API 更新',
  arica_api_update: 'ARICA API 更新',
};

// 受信箱の毎朝 1 行ダイジェスト用: open findings を ①取込 / ②提案 / ③登録 の 3 層に分ける
export const FINDING_LAYER_OF_KIND: Record<string, 'intake' | 'proposal' | 'register'> = {
  arica_fetch_platform_stuck: 'intake',
  arica_fetch_email_stuck: 'intake',
  vendor_invoice_missing: 'intake',
  invoice_unenrolled_newcomers: 'proposal',
  timee_invoice_unbuilt: 'proposal',
  duplicate_deal: 'proposal',
  bank_analysis_stalled: 'proposal',
  batch_verify_failed: 'register',
  batch_unverifiable: 'register',
  job_dead_letter: 'register',
};

// severity → ラベル + 色 (high=赤 / medium=橙 / low=グレー)
export const FINDING_SEVERITY_META: Record<string, { label: string; bg: string; fg: string; bd: string }> = {
  high: { label: '高', bg: C.dangerBg, fg: C.danger, bd: '#e6b0ac' },
  medium: { label: '中', bg: C.warnBg, fg: C.warn, bd: '#e3c68a' },
  low: { label: '低', bg: '#f1f4f7', fg: C.sub, bd: '#ccd6df' },
};

// status バッジは統一語彙 (lib/statusVocab.ts) — open = 対応待ち(監査確認) = 橙 (裁定待ち。失敗ではない)
const STATUS_META: Record<string, [string, string, string, string]> = Object.fromEntries(
  (['open', 'confirmed', 'dismissed'] as const).map((s) => {
    const u = findingStatusToUnified(s);
    return [s, [u.label, u.bg, u.fg, u.bd]];
  }),
) as Record<string, [string, string, string, string]>;

const fmtTs = (t: number | string | null | undefined): string => {
  if (t == null || t === '') return '—';
  const d = new Date(t as any);
  if (isNaN(d.getTime())) return String(t);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const pretty = (s: string | null | undefined): string | null => {
  if (!s) return null;
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
};

// batch 系 finding から遷移先 batchId を導出 (evidence.batch_id 優先 → target_id)
const batchIdOf = (f: Finding): string | null => {
  try {
    const ev = JSON.parse(f.evidence_json || 'null');
    if (ev && ev.batch_id) return String(ev.batch_id);
  } catch { /* ignore */ }
  const tt = (f.target_type || '').toLowerCase();
  if (!tt.includes('batch')) return null;
  const tid = String(f.target_id || '');
  if (!tid) return null;
  // batch item id が 'batchId:seq' 形式の場合は前半を採用
  return tid.includes(':') ? tid.split(':')[0] : tid;
};

const TargetCell: React.FC<{ f: Finding }> = ({ f }) => {
  const tt = (f.target_type || '').toLowerCase();
  if (tt.includes('deal')) {
    return (
      <a
        href={`https://secure.freee.co.jp/deals#deal_id=${encodeURIComponent(String(f.target_id))}`}
        target="_blank" rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ color: '#476b8e', fontFamily: NUM, fontSize: 11 }}
      >
        freee取引 {f.target_id} ↗
      </a>
    );
  }
  const bid = batchIdOf(f);
  if (bid) {
    return (
      <a
        href={`#batches/${bid}`}
        onClick={(e) => e.stopPropagation()}
        style={{ color: '#476b8e', fontFamily: NUM, fontSize: 11 }}
      >
        バッチ台帳 {bid}
      </a>
    );
  }
  // job ほか: リンク先なし → id 表示のみ
  return <span style={{ color: C.sub, fontFamily: NUM, fontSize: 11 }}>{f.target_type} {f.target_id}</span>;
};

export const FindingsPage: React.FC = () => {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [severity, setSeverity] = useState('');
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ finding: Finding; events: FindingEvent[] } | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dismissId, setDismissId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchFindings({ status: status || undefined, kind: kind || undefined, severity: severity || undefined });
      setFindings(r.findings || []);
      setMsg(null);
    } catch (e: unknown) {
      setMsg(`取得失敗 (/api/findings): ${e instanceof Error ? e.message : String(e)}`);
    } finally { setLoading(false); }
  }, [status, kind, severity]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null); setDetailErr(null);
    try { setDetail(await fetchFindingDetail(id)); }
    catch (e: unknown) { setDetailErr(e instanceof Error ? e.message : String(e)); }
  }, []);

  const toggleDetail = useCallback((id: string) => {
    if (openId === id) { setOpenId(null); setDetail(null); setDetailErr(null); return; }
    setOpenId(id);
    setDismissId(null); setDismissReason('');
    loadDetail(id);
  }, [openId, loadDetail]);

  const refreshAfterAction = useCallback(async (id: string) => {
    await load();
    await loadDetail(id);
  }, [load, loadDetail]);

  const doConfirm = useCallback(async (id: string) => {
    setBusyId(id);
    try { await confirmFinding(id); await refreshAfterAction(id); }
    catch (e: unknown) { setMsg(`確認失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [refreshAfterAction]);

  const doDismiss = useCallback(async (id: string, reason: string) => {
    if (!reason.trim()) return;
    setBusyId(id);
    try {
      await dismissFinding(id, reason.trim());
      setDismissId(null); setDismissReason('');
      await refreshAfterAction(id);
    } catch (e: unknown) { setMsg(`却下失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [refreshAfterAction]);

  const doReopen = useCallback(async (id: string) => {
    setBusyId(id);
    try { await reopenFinding(id); await refreshAfterAction(id); }
    catch (e: unknown) { setMsg(`再open失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [refreshAfterAction]);

  // 請求書未着 (vendor_invoice_missing) 専用: 「今月なし」(当サイクルのみ) / 「取引終了」(恒久除外、理由 2 種)
  const [excludeId, setExcludeId] = useState<string | null>(null);
  const doNoInvoice = useCallback(async (id: string) => {
    setBusyId(id);
    try { await dismissFindingNoInvoiceThisCycle(id); await refreshAfterAction(id); }
    catch (e: unknown) { setMsg(`「今月なし」失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [refreshAfterAction]);
  const doExcludeVendor = useCallback(async (id: string, reason: 'ended' | 'irregular') => {
    setBusyId(id);
    try {
      await excludeVendorFinding(id, reason);
      setExcludeId(null);
      await refreshAfterAction(id);
    } catch (e: unknown) { setMsg(`「取引終了」失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [refreshAfterAction]);

  const doSweep = useCallback(async () => {
    setSweeping(true);
    try { await sweepFindings(); await load(); }
    catch (e: unknown) { setMsg(`スイープ失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setSweeping(false); }
  }, [load]);

  const openCount = findings.filter((f) => f.status === 'open').length;

  return (
    <div style={S.page}>
      <Container>
        <section style={S.sheet}>
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <h1 style={S.topTitle}>監査</h1>
              <span style={S.topCaption}>スイープ (worker 決定論検査) が検出した指摘の確認・却下台帳 — {ACTION_HINT.judge}: 「確認済にする」か理由付きで「却下」</span>
              <span style={S.pill}><MdWarningAmber size={13} /> 対応待ち(監査確認) {openCount}件</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button appearance="primary" small onClick={doSweep} disabled={sweeping || loading}>
                {sweeping ? <InlineSpinner isLoading /> : <MdSearch />} 今すぐスイープ
              </Button>
              <Button appearance="secondary" small onClick={load} disabled={loading}>
                {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 更新
              </Button>
            </div>
          </header>

          {/* フィルタ: kind / severity / status */}
          <div style={S.filterBar}>
            <span style={S.filterLabel}>種別</span>
            <select style={S.select} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">全て</option>
              {Object.entries(FINDING_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <span style={S.filterLabel}>重要度</span>
            <select style={S.select} value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">全て</option>
              <option value="high">高 (high)</option>
              <option value="medium">中 (medium)</option>
              <option value="low">低 (low)</option>
            </select>
            <span style={S.filterLabel}>状態</span>
            <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="open">対応待ち(監査確認) (open)</option>
              <option value="confirmed">完了(確認済)</option>
              <option value="dismissed">除外(却下)</option>
              <option value="">全て</option>
            </select>
          </div>

          {msg && <div style={{ padding: 12 }}><MessageBlock error>{msg}</MessageBlock></div>}

          <section style={S.list}>
            {findings.length === 0 && !loading && !msg && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontWeight: 700 }}>
                該当する監査指摘はありません。スイープは定期的に自動実行されます。[今すぐスイープ]で即時検査もできます。
              </div>
            )}
            {findings.map((f) => {
              const sev = FINDING_SEVERITY_META[f.severity] || FINDING_SEVERITY_META.low;
              const [stLabel, stBg, stFg, stBd] = STATUS_META[f.status] || [f.status, '#eee', '#333', '#ccc'];
              const isOpen = openId === f.id;
              const d = detail && detail.finding && detail.finding.id === f.id ? detail : null;
              const cur = d ? d.finding : f;
              const busy = busyId === f.id;
              return (
                <div key={f.id} style={S.card(f.status === 'open' ? (f.severity === 'high' ? 'danger' : 'action') : 'none')}>
                  <div style={S.cardHead} onClick={() => toggleDetail(f.id)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={S.badge(sev.bg, sev.fg, sev.bd)}>{sev.label}</span>
                        <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>{FINDING_KIND_LABEL[f.kind] || f.kind}</span>
                        <span style={S.badge(stBg, stFg, stBd)}>{stLabel}</span>
                        <b style={{ fontSize: 13 }}>{f.summary}</b>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ ...S.sub, fontFamily: NUM }}>{f.id}</span>
                        <span style={{ ...S.sub, fontFamily: NUM }}>{fmtTs(f.created_at)}</span>
                        <TargetCell f={f} />
                      </div>
                    </div>
                    <span style={{ ...S.sub, fontWeight: 800 }}>{isOpen ? '閉じる' : '詳細'}</span>
                  </div>

                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${C.lineDark}`, padding: '12px 15px 14px', background: '#fbfcfd' }}>
                      {detailErr && (
                        <MessageBlock error>詳細取得失敗 (/api/findings/{f.id}): {detailErr} — 一覧の情報のみ表示しています。</MessageBlock>
                      )}
                      {!d && !detailErr && <div style={{ padding: 8 }}><InlineSpinner isLoading /> 詳細を取得中…</div>}

                      {/* 操作: confirm / dismiss(理由必須) / reopen */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0 4px' }}>
                        {cur.status === 'open' ? (
                          <>
                            <Button appearance="primary" small disabled={busy} onClick={() => doConfirm(f.id)}>
                              {busy ? <InlineSpinner isLoading /> : '確認済にする'}
                            </Button>
                            {dismissId === f.id ? (
                              <>
                                <input
                                  style={S.reasonInput}
                                  placeholder="却下する理由 (必須・監査記録に残ります)"
                                  value={dismissReason}
                                  autoFocus
                                  onChange={(e) => setDismissReason(e.target.value)}
                                />
                                <Button appearance="primary" small disabled={!dismissReason.trim() || busy} onClick={() => doDismiss(f.id, dismissReason)}>
                                  {busy ? <InlineSpinner isLoading /> : '却下確定'}
                                </Button>
                                <Button appearance="secondary" small disabled={busy} onClick={() => { setDismissId(null); setDismissReason(''); }}>取消</Button>
                              </>
                            ) : (
                              <Button appearance="secondary" small disabled={busy} onClick={() => { setDismissId(f.id); setDismissReason(''); }}>却下</Button>
                            )}
                            {/* 請求書未着 専用の 2 ボタン (2026-10-21): 今月なし = 当サイクルのみ / 取引終了 = 恒久除外 */}
                            {cur.kind === 'vendor_invoice_missing' && dismissId !== f.id && (
                              excludeId === f.id ? (
                                <>
                                  <span style={S.sub}>取引終了の理由:</span>
                                  <Button appearance="primary" small disabled={busy} onClick={() => doExcludeVendor(f.id, 'ended')}>
                                    {busy ? <InlineSpinner isLoading /> : '終了 (再着荷で自動解除)'}
                                  </Button>
                                  <Button appearance="secondary" small disabled={busy} onClick={() => doExcludeVendor(f.id, 'irregular')}>不定期・隔月 (恒久)</Button>
                                  <Button appearance="secondary" small disabled={busy} onClick={() => setExcludeId(null)}>取消</Button>
                                </>
                              ) : (
                                <>
                                  <Button appearance="secondary" small disabled={busy} onClick={() => doNoInvoice(f.id)}>今月なし</Button>
                                  <Button appearance="secondary" small disabled={busy} onClick={() => setExcludeId(f.id)}>取引終了</Button>
                                </>
                              )
                            )}
                          </>
                        ) : (
                          <Button appearance="secondary" small disabled={busy} onClick={() => doReopen(f.id)}>
                            {busy ? <InlineSpinner isLoading /> : '再open'}
                          </Button>
                        )}
                      </div>

                      {/* 提案される対処 */}
                      {pretty(cur.proposed_fix_json) && (
                        <div style={{ marginTop: 10 }}>
                          <div style={S.subhead}>提案される対処 (proposed_fix)</div>
                          <pre style={S.pre}>{pretty(cur.proposed_fix_json)}</pre>
                        </div>
                      )}

                      {/* 証跡 */}
                      <div style={{ marginTop: 10 }}>
                        <div style={S.subhead}>証跡 (evidence)</div>
                        <pre style={S.pre}>{pretty(cur.evidence_json) || '—'}</pre>
                      </div>

                      {/* イベントタイムライン */}
                      <div style={{ marginTop: 10 }}>
                        <div style={S.subhead}>イベント履歴</div>
                        {(d?.events || []).length === 0 && (
                          <div style={{ color: C.sub, fontSize: 12, padding: '2px 0' }}>
                            {d ? 'イベントはまだありません' : '—'}
                          </div>
                        )}
                        {(d?.events || []).map((ev, i) => (
                          <div key={ev.id ?? i} style={{ display: 'flex', gap: 10, padding: '4px 0', borderBottom: `1px dashed ${C.line}`, fontSize: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: NUM, color: C.sub, minWidth: 108 }}>{fmtTs(ev.ts ?? ev.at ?? ev.created_at)}</span>
                            <b>{ev.event || ev.type || ev.action || 'event'}</b>
                            {(ev.by || ev.actor) && <span style={{ color: C.sub }}>{ev.by || ev.actor}</span>}
                            {ev.reason && <span style={{ color: C.warn }}>理由: {ev.reason}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </section>
      </Container>
    </div>
  );
};

export default FindingsPage;
